import { useState, useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { bigDecimal } from "js-big-decimal";
import Translations from "@translate/hooks/useHistoricalEarningsData";
import { I18nUtil } from "@utils/i18n";
import type { HistoricalEarningsData } from "@components/StockDetails/StockEarning/types";
import {
  calculateSurprise,
  determineResult,
  extractEPSFromFormatted,
  extractFormattedValue,
  extractRevenueNumber,
  formatEPSValue,
  formatRevenueDisplay,
  formatSurprise,
} from "@utils/earnings";

const I18n = I18nUtil.createScoped(Translations);

// Result 颜色配置
const RESULT_COLORS = {
  BEAT: "text-content_accent1_primary",
  MISSED: "text-content_accent2_primary",
  "--": "text-content_tertiary",
} as const;

type PeriodType = "quarterly" | "annual";
type MetricType = "eps" | "revenue";

/**
 * 年度数据聚合函数
 */
function aggregateToAnnual(
  quarterlyData: HistoricalEarningsData[],
): HistoricalEarningsData[] {
  if (!quarterlyData.length) {
    return [];
  }

  // 按年份分组
  const yearMap = new Map<number, HistoricalEarningsData[]>();

  quarterlyData.forEach((item) => {
    if (!yearMap.has(item.year)) {
      yearMap.set(item.year, []);
    }
    yearMap.get(item.year)!.push(item);
  });

  // 对每个年份进行聚合
  const annualData: HistoricalEarningsData[] = [];
  yearMap.forEach((quarters, year) => {
    // 使用 BigDecimal 进行精确累加（从显示字段中提取数字）
    let totalEpsEstimate = "0";
    let totalEpsActual = "0";
    let totalRevenueEstimate = "0";
    let totalRevenueActual = "0";

    quarters.forEach((q) => {
      // 从显示字段中提取数字进行累加
      const epsEstimate =
        extractEPSFromFormatted(q.eps_estimate)?.toString() ?? "0";
      const epsActual =
        extractEPSFromFormatted(q.eps_actual)?.toString() ?? "0";
      const revenueEstimate = extractRevenueNumber(q.revenue_estimate);
      const revenueActual = extractRevenueNumber(q.revenue_actual);

      totalEpsEstimate = bigDecimal.add(totalEpsEstimate, epsEstimate);
      totalEpsActual = bigDecimal.add(totalEpsActual, epsActual);
      totalRevenueEstimate = bigDecimal.add(
        totalRevenueEstimate,
        revenueEstimate,
      );
      totalRevenueActual = bigDecimal.add(totalRevenueActual, revenueActual);
    });

    // 转换为数字便于处理
    const annualEpsEstimate = parseFloat(totalEpsEstimate);
    const annualEpsActual = parseFloat(totalEpsActual);
    const annualRevenueEstimate = parseFloat(totalRevenueEstimate);
    const annualRevenueActual = parseFloat(totalRevenueActual);

    // 格式化显示值
    const formattedEpsActual = formatEPSValue(annualEpsActual);
    const formattedEpsEstimate = formatEPSValue(annualEpsEstimate);
    const formattedRevenueEstimate = formatRevenueDisplay(
      annualRevenueEstimate,
    );
    const formattedRevenueActual = formatRevenueDisplay(annualRevenueActual);

    // 步骤1: 基于格式化后的值计算 Surprise（确保与用户屏幕显示的值一致）
    const epsSurprise = calculateSurprise(
      extractEPSFromFormatted(formattedEpsActual),
      extractEPSFromFormatted(formattedEpsEstimate),
    );
    const revenueSurprise = calculateSurprise(
      extractFormattedValue(annualRevenueActual),
      extractFormattedValue(annualRevenueEstimate),
    );

    // 生成年度记录
    annualData.push({
      fiscal_period: year.toString(),
      year,
      quarter: undefined,

      eps_estimate: formattedEpsEstimate,
      eps_actual: formattedEpsActual,
      eps_surprise: formatSurprise(epsSurprise),
      eps_result: determineResult(epsSurprise),

      revenue_estimate: formattedRevenueEstimate,
      revenue_actual: formattedRevenueActual,
      revenue_surprise: formatSurprise(revenueSurprise),
      revenue_result: determineResult(revenueSurprise),
    });
  });

  // 按年份倒序排序
  return annualData.sort((a, b) => b.year - a.year);
}

/**
 * 生成 Columns 配置
 */
function generateColumns(
  metric: MetricType,
  isPC: boolean,
): ColumnDef<HistoricalEarningsData>[] {
  const baseColumns: ColumnDef<HistoricalEarningsData>[] = [
    {
      accessorKey: "fiscal_period",
      id: "fiscal_period",
      header: I18n.t("Fiscal Period"),
      meta: {
        width: 213,
        pinned: "left" as const,
        cellClassName: "bg-background_solid_secondary",
      },
    },
  ];

  if (metric === "eps") {
    return [
      ...baseColumns,
      {
        accessorKey: "eps_estimate",
        id: "eps_estimate",
        header: I18n.t("Estimated"),
        meta: {
          width: 136,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "eps_actual",
        id: "eps_actual",
        header: I18n.t("Reported"),
        meta: {
          width: 136,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "eps_surprise",
        id: "eps_surprise",
        header: I18n.t("Surprise"),
        meta: {
          width: 136,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "eps_result",
        id: "eps_result",
        header: I18n.t("Result"),
        meta: {
          width: 86,
        },
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <span
              className={RESULT_COLORS[value as keyof typeof RESULT_COLORS]}
            >
              {value}
            </span>
          );
        },
      },
    ];
  } else {
    return [
      ...baseColumns,
      {
        accessorKey: "revenue_estimate",
        id: "revenue_estimate",
        header: I18n.t("Estimated"),
        meta: {
          width: isPC ? "1fr" : 150,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "revenue_actual",
        id: "revenue_actual",
        header: I18n.t("Reported"),
        meta: {
          width: isPC ? "1fr" : 150,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "revenue_surprise",
        id: "revenue_surprise",
        header: I18n.t("Surprise"),
        meta: {
          width: isPC ? "1fr" : 150,
          cellClassName: "text-left",
        },
      },
      {
        accessorKey: "revenue_result",
        id: "revenue_result",
        header: I18n.t("Result"),
        meta: {
          width: isPC ? "1fr" : 86,
        },
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <span
              className={RESULT_COLORS[value as keyof typeof RESULT_COLORS]}
            >
              {value}
            </span>
          );
        },
      },
    ];
  }
}

/**
 * HistoricalEarnings 专用 Hook
 */
export function useHistoricalEarningsData(
  allValidQuarters: HistoricalEarningsData[],
  isPC: boolean,
) {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("quarterly");
  const [selectedMetric, setSelectedMetric] = useState<MetricType>("eps");

  // 季度数据：最近 5 个
  const quarterlyData = useMemo(
    () => allValidQuarters.slice(0, 5),
    [allValidQuarters],
  );

  // 年度数据：聚合并取最近 3 年
  const annualData = useMemo(() => {
    const aggregated = aggregateToAnnual(allValidQuarters);
    return aggregated.slice(0, 3);
  }, [allValidQuarters]);

  // 根据维度选择展示数据
  const displayData = useMemo(() => {
    return selectedPeriod === "quarterly" ? quarterlyData : annualData;
  }, [selectedPeriod, quarterlyData, annualData]);

  // 根据指标生成 columns 配置
  const columns = useMemo(
    () => generateColumns(selectedMetric, isPC),
    [selectedMetric, isPC],
  );

  return {
    displayData,
    columns,
    selectedPeriod,
    selectedMetric,
    setSelectedPeriod,
    setSelectedMetric,
    quarterlyData,
    annualData,
  };
}
